import countries from "@/configs/countries";
import {CurrencySettings} from "@/extensions/services/currency-settings";
import {
    getBrowserCountry,
    getBrowserLanguage,
} from "@/extensions/services/google-chrome-available-languages";

/**
 * @typedef {object} Currency
 * @property {string} code
 * @property {string} name
 * @property {string} symbol
 */

/**
 * @typedef {object} Language
 * @property {string} iso639_1
 * @property {string} iso639_2
 * @property {string} name
 * @property {string} nativeName
 */

/**
 * @typedef {object} Country
 * @property {string} alpha2Code
 * @property {string[]} altSpellings
 * @property {Currency[]} currencies
 * @property {Language[]} languages
 * @property {string} name
 * @property {string} nativeName
 */

/** @typedef {import('../../configs/countries').CountryConfig} */

/** @type {string[]} */
const CURRENCIES = Object.keys(CurrencySettings)
    .map((key) => (["default"].includes(key) ? null : key))
    .filter(Boolean)
    .sort();

/** @type {string[]} */
const expectedCountries = ["TF"];

/**
 * Check string value is type of alphabetical.
 *
 * @param {string} word
 * @return {boolean}
 */
const isAlpha = (word) => /^\w/.test(word);

/**
 * Simple map for object keys.
 *
 * @param {boolean} condition
 * @return {string}
 */
const getKey = (condition) => (condition ? "text" : "value");

/**
 * Filter array by expected items.
 *
 * @param {CountryConfig} item
 * @param {string} item.value
 * @return {boolean}
 */
const countryFilter = ({value}) => !expectedCountries.includes(value);

/**
 * Sort countries by text or value, depends on type of value.
 *
 * @param {CountryConfig} next
 * @param {CountryConfig} prev
 * @return {number}
 */
const countrySorter = (next, prev) => {
    next = next[getKey(isAlpha(next.text))];
    prev = prev[getKey(isAlpha(prev.text))];

    return next.toString().localeCompare(prev.toString());
};

/**
 * Unique any type of array.
 *
 * @param {any[]} array
 * @return {any[]}
 */
const unique = (array) => Array.from(new Set(array));

// testGoogleChromeAvailableLanguages();

export default {
    /**
     *
     * @return {{text: string, value: string}[]}
     */
    getLanguages() {
        const languages = [
            {value: "bg", text: "български език"},
            {value: "bn", text: "বাংলা"},
            {value: "cs", text: "čeština"},
            {value: "zh", text: "中文"},
            {value: "da", text: "dansk"},
            {value: "en", text: "English"},
            {value: "fi", text: "Suomi"},
            {value: "fr", text: "français"},
            {value: "de", text: "Deutsch"},
            {value: "el", text: "ελληνικά"},
            {value: "hi", text: "हिन्दी"},
            {value: "id", text: "Bahasa Indonesia"},
            {value: "it", text: "Italiano"},
            {value: "ko", text: "한국어"},
            {value: "ja", text: "日本語 (にほんご)"},
            {value: "hu", text: "Magyar"},
            {value: "ms", text: "Bahasa Melayu"},
            {value: "nl", text: "Nederlands"},
            {value: "no", text: "Norsk"},
            {value: "pl", text: "język polski"},
            {value: "pt", text: "Português"},
            {value: "ro", text: "Română"},
            {value: "ru", text: "Русский"},
            {value: "sk", text: "slovenčina"},
            {value: "sl", text: "slovenski jezik"},
            {value: "es", text: "Español"},
            {value: "tr", text: "Türkçe"},
            {value: "th", text: "ไทย"},
            {value: "uk", text: "Українська"},
            {value: "vi", text: "Tiếng Việt"},
        ];

        if (localStorage.getItem('isDevMode') === 'true') {
            languages.unshift({value: 'bn', text: 'বাংলা (dev)'});
            languages.unshift({value: 'bg', text: 'български език (dev)'});
            languages.unshift({value: 'cs', text: 'čeština (dev)'});
            languages.unshift({value: 'da', text: 'dansk (dev)'});
            languages.unshift({value: 'de', text: 'Deutsch (dev)'});
            languages.unshift({value: "el", text: "ελληνικά (dev)"});
            languages.unshift({value: "es", text: "Español (dev)"});
            languages.unshift({value: 'fr', text: 'français (dev)'});
            languages.unshift({value: 'hi', text: 'हिन्दी (dev)'});
            languages.unshift({value: 'hr', text: 'Croatian (dev)'});
            languages.unshift({value: 'id', text: 'Bahasa Indonesia (dev)'});
            languages.unshift({value: 'it', text: 'Italiano (dev)'});
            languages.unshift({value: 'ja', text: '日本語 (にほんご) (dev)'});
            languages.unshift({value: 'ko', text: "한국어 (dev)"});
            languages.unshift({value: 'ms', text: "Bahasa Melayu (dev)"});
            languages.unshift({value: 'no', text: 'Norsk (dev)'});
            languages.unshift({value: "pl", text: "język polski (dev)"}),
            languages.unshift({value: 'pt', text: 'Português (dev)'});
            languages.unshift({value: 'ro', text: "Română (dev)"});
            languages.unshift({value: 'ru', text: "Русский (dev)"});
            languages.unshift({value: "sk", text: "slovenčina (dev)"});
            languages.unshift({value: "sl", text: "slovenski jezik (dev)"});
            languages.unshift({value: "th", text: "ไทย (dev)"});
            languages.unshift({value: "tr", text: "Türkçe (dev)"});
            languages.unshift({value: "uk", text: "Українська (dev)"});
            languages.unshift({value: 'vi', text: "Tiếng Việt (dev)"});
            languages.unshift({value: 'zh', text: "中文 (dev)"});
        }

        return languages;
    },
    /**
     *
     * @return {CountryConfig[]}
     */
    getCountries() {
        return unique(countries.filter(countryFilter).sort(countrySorter));
    },
    getDefaultCountry(fallbackCountry = "US") {
        // TODO: check case of try-catch
        try {
            const countries = this.getCountries();
            const country = getBrowserCountry();
            const found = Boolean(countries.find(({value}) => value === country));

            return found ? country : fallbackCountry;
        } catch (error) {
            captureException(error);

            return fallbackCountry;
        }
    },
    getDefaultLanguage(fallbackLanguage = "en") {
        // TODO: check case of try-catch
        try {
            const languages = this.getLanguages();
            const language = getBrowserLanguage();
            const found = Boolean(languages.find(({value}) => value === language));

            return found ? language : fallbackLanguage;
        } catch (error) {
            captureException(error);

            return fallbackLanguage;
        }
    },
    getCurrencies() {
        /** @type {Map<string, object>} */
        const ret = new Map();

        window.CountryJson.forEach((item) => {
            item.currencies.forEach((currency) => {
                if (typeof currency.code === "undefined" || currency.code.toLowerCase() === "(none)") {
                    return;
                }

                const code = currency.code.toUpperCase();

                if (!CURRENCIES.includes(code)) {
                    return;
                }

                ret.set(code, {value: code, text: `${code} - ${currency.name}`});
            });
        });

        return Array.from(ret.values()).sort((a, b) => a.value.localeCompare(b.value));
    },
    /**
     *
     * @param {string} fallbackCurrency
     * @return {string}
     */
    getDefaultCurrency(fallbackCurrency = "USD") {
        try {
            /** @type {string|null} */
            const country = app.$store.state.localSettings.country || this.getDefaultCountry();

            if (!country) {
                return fallbackCurrency;
            }

            /**
             *
             * @param {Country} item
             * @return {any[]|null}
             */
            const countryMapper = (item) => (item.alpha2Code === country ? item.currencies[0].code : null);

            /** @type {string|string[]|null} */
            let currency = window.CountryJson.map(countryMapper).filter(Boolean);

            if (!currency) {
                return fallbackCurrency;
            }

            currency = Array.isArray(currency) ? currency[0] : currency;

            return CURRENCIES.includes(currency) ? currency : fallbackCurrency;
        } catch (error) {
            captureException(error);
        }

        return fallbackCurrency;
    },
    getDefaultNotation(fallbackNotation = "ISO") {
        try {
            const country = app.$store.state.localSettings.country || this.getDefaultCountry();

            return country === "US" ? "USA" : fallbackNotation;
        } catch (error) {
            captureException(error);

            return fallbackNotation;
        }
    },
};
